// Content type constants
export enum ContentType {
  IMAGE = "IMAGE",
  QUIZ = "QUIZ",
  TEXT = "TEXT",
  WORKSHEET = "WORKSHEET",
  CODE = "CODE",
  VIDEO = "VIDEO",
  AUDIO = "AUDIO",
}

type StoryboardContentType = `${ContentType}`;

type SubTypeByContent = {
  TEXT: TextSubType;
  QUIZ: QuizSubType;
  IMAGE: ImageSubType;
  CODE: CodeSubType;
  VIDEO: "EXTERNAL_VIDEO";
  AUDIO: "EXTERNAL_AUDIO";
  WORKSHEET: "WORKSHEET";
};

/** Ties a storyboard variant's `subType` to the subtypes valid for its `contentType`. */
type Variant<
  C extends StoryboardContentType,
  S extends SubTypeByContent[C],
  Payload,
> = {
  contentType: C;
  subType: S;
} & Payload;

// Specific storyboard types
type TextSubType =
  | "CHECKLIST"
  | "FLASHCARDS"
  | "QUESTION"
  | "LEARNING_OBJECTIVE_QUESTION"
  | "LEARNING_OBJECTIVE_ASSERTION"
  | "WORKSHEET"
  | "SUMMARY"
  | "EXAMPLE"
  | "HINT"
  | "TEXT"
  | "SELF_REFLECTION_QUESTIONS"
  | "EXPERIENCE_REFLECTION"
  | "DIALOGUE"
  | "ANALOGY"
  | "PROVOCATIVE_QUESTION"
  | "FALLBACK"
  | "EXAMPLE_PROBLEMSOLVING";

export type TextStoryboard =
  | Variant<"TEXT", "CHECKLIST", { text: TextChecklistData }>
  | Variant<"TEXT", "FLASHCARDS", { text: TextFlashcardsData }>
  | Variant<"TEXT", "QUESTION", { text: string[] }>
  | Variant<
      "TEXT",
      "LEARNING_OBJECTIVE_QUESTION",
      { text: TextLearningObjectiveQuestionData }
    >
  | Variant<"TEXT", "LEARNING_OBJECTIVE_ASSERTION", { text: TextIntroBodyData }>
  | Variant<"TEXT", "WORKSHEET", { text: WorksheetData }>
  | Variant<"TEXT", "SUMMARY", { text: TextTitledBodyData }>
  | Variant<"TEXT", "EXAMPLE", { text: TextTitledBodyData | string }>
  | Variant<"TEXT", "HINT", { text: TextIntroBodyData }>
  | Variant<
      "TEXT",
      "TEXT",
      { text: TextStructuredContentData | TextTitledBodyData | string }
    >
  | Variant<
      "TEXT",
      "SELF_REFLECTION_QUESTIONS",
      { text: TextSelfReflectionQuestionsData | string }
    >
  | Variant<
      "TEXT",
      "EXPERIENCE_REFLECTION",
      { text: TextExperienceReflectionData | string }
    >
  | Variant<"TEXT", "DIALOGUE", { text: TextTitledBodyData | string }>
  | Variant<"TEXT", "ANALOGY", { text: TextTitledBodyData | string }>
  | Variant<"TEXT", "PROVOCATIVE_QUESTION" | "FALLBACK", { text: string }>
  | Variant<
      "TEXT",
      "EXAMPLE_PROBLEMSOLVING",
      { text: TextTitledBodyData | string }
    >;

type CodeSubType =
  | "CODE_EXAMPLE"
  | "CODE_THEORY"
  | "CODE_ANALOGY"
  | "CODE_QUESTION"
  | "CODE_EXAMPLE_PROBLEMSOLVING";

export type CodeStoryboard =
  | Variant<"CODE", "CODE_EXAMPLE", { text: CodeExampleData }>
  | Variant<"CODE", "CODE_THEORY", { text: CodeTheoryData }>
  | Variant<"CODE", "CODE_ANALOGY", { text: TextTitledBodyData | string }>
  | Variant<"CODE", "CODE_QUESTION", { text: CodeQuestionData }>
  | Variant<
      "CODE",
      "CODE_EXAMPLE_PROBLEMSOLVING",
      { text: CodeExampleProblemSolvingData }
    >;

type QuizSubType =
  | "FREETEXT"
  | "TRUE_FALSE"
  | "MULTIPLE_CHOICE"
  | "CODE_MULTIPLE_CHOICE"
  | "CLOZE_FREETEXT"
  | "CLOZE_DROPDOWN"
  | "MATCHING_DEFINITION"
  | "MATCHING_SENTENCE"
  | "CODE_TRUE_FALSE"
  | "CODE_FREETEXT"
  | "CODE_DEBUG"
  | "CODE_ASSERTION"
  | "CODE_EXPLANATION";

/**
 * The generator titles every quiz now. Older storyboards have none, and the
 * renderer falls back to the content element's own title.
 */
export type QuizStoryboard =
  | Variant<
      "QUIZ",
      "FREETEXT",
      { title?: string; instruction: QuizFreeTextEntryData["instruction"] }
    >
  | Variant<
      "QUIZ",
      "TRUE_FALSE",
      { title?: string; entries: QuizTrueFalseEntryData[] }
    >
  | Variant<
      "QUIZ",
      "MULTIPLE_CHOICE" | "CODE_MULTIPLE_CHOICE",
      { title?: string; entries: QuizMultipleChoiceEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CLOZE_FREETEXT",
      { title?: string; entries: QuizGapwordFreeTextEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CLOZE_DROPDOWN",
      { title?: string; entries: QuizDropdownEntryData[] }
    >
  | Variant<
      "QUIZ",
      "MATCHING_DEFINITION" | "MATCHING_SENTENCE",
      { title?: string; entries: QuizMatchingEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CODE_TRUE_FALSE",
      { title?: string; entries: QuizCodeTrueFalseEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CODE_FREETEXT",
      { title?: string; entries: QuizCodeFreetextEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CODE_DEBUG",
      { title?: string; entries: QuizCodeDebugEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CODE_ASSERTION",
      { title?: string; entries: QuizCodeAssertionEntryData[] }
    >
  | Variant<
      "QUIZ",
      "CODE_EXPLANATION",
      { title?: string; entries: QuizCodeExplanationEntryData[] }
    >;

type ImageSubType = "DEMONSTRATION" | "LABELED_GRAPHIC";

export type ImageStoryboard =
  | Variant<
      "IMAGE",
      "DEMONSTRATION",
      { description: ImageDemonstrationData | string }
    >
  | Variant<
      "IMAGE",
      "LABELED_GRAPHIC",
      { description: ImageLabeledGraphicData }
    >;

// VIDEO types
export interface VideoElementData {
  /** Optional heading shown above the video */
  title?: string;
  /** Optional markdown introduction shown between title and video */
  introduction?: string;
  provider: "YOUTUBE" | "VIMEO";
  videoId: string;
  /** Vimeo privacy hash for unlisted videos */
  embedHash?: string | null;
  /** Canonical watch URL (informational) */
  url?: string;
  /** Provider thumbnail, captured during validation in the Content Factory */
  thumbnailUrl?: string | null;
}

export type VideoStoryboard = Variant<
  "VIDEO",
  "EXTERNAL_VIDEO",
  { video: VideoElementData }
>;

// AUDIO types (external audio = Vimeo/YouTube video with still image)
export type AudioStoryboard = Variant<
  "AUDIO",
  "EXTERNAL_AUDIO",
  { audio: VideoElementData }
>;

export type WorksheetStoryboard = Variant<
  "WORKSHEET",
  "WORKSHEET",
  { text: WorksheetData }
>;

export type Storyboard = (
  | TextStoryboard
  | QuizStoryboard
  | ImageStoryboard
  | WorksheetStoryboard
  | CodeStoryboard
  | VideoStoryboard
  | AudioStoryboard
) & { contentElementId: string; files?: StoryboardFile[] };

// TEXT types
export interface TextTitledBodyData {
  title: string;
  body?: string;
  /** Older structured payloads used `text` instead of `body`. */
  text?: string;
}

export interface TextIntroBodyData {
  title?: string;
  introduction: string;
  text: string;
}

export interface TextSectionData {
  heading: string;
  text: string;
}

export interface TextStructuredContentData {
  title: string;
  introduction: string;
  sections: TextSectionData[];
}

export interface TextQuestionsData {
  title?: string;
  introduction: string;
  questions: string[];
}

export interface TextSelfReflectionQuestionItem {
  text: string;
}

export interface TextSelfReflectionQuestionsData {
  title?: string;
  introduction: string;
  questions: TextSelfReflectionQuestionItem[];
}

export interface TextExperienceReflectionData {
  /** Absent on storyboards generated before the titles rollout. */
  title?: string;
  /** The single reflection question. */
  question: string;
  /** Scenario hint shown as the intro above the question. */
  instruction: string;
}

export interface TextFlashcardData {
  frontside: string;
  backside: string;
}

export interface TextFlashcardsData {
  /** Absent on storyboards generated before the titles rollout. */
  title?: string;
  instruction?: string;
  introduction: string[];
  cards: TextFlashcardData[];
}

export interface TextChecklistSectionData {
  heading: string;
  items: string[];
}

export interface TextChecklistData {
  title: string;
  introduction: string;
  instruction: string;
  sections: TextChecklistSectionData[];
}

export interface TextLearningObjectiveQuestionData {
  title?: string;
  introduction: string;
  questions: string[];
}

// QUIZ types
export interface UserFeedback {
  positive: string;
  negative: string;
}

/** Shared by quiz entries that carry a plain instruction + positive/negative feedback pair. */
interface QuizFeedbackEntryBase {
  instruction: string;
  userFeedback: UserFeedback;
}

export interface QuizTrueFalseEntryData extends QuizFeedbackEntryBase {
  statement: string;
}

export interface QuizDropdownEntryData extends QuizFeedbackEntryBase {
  gaps: {
    options: string[];
    correctAnswer: string;
  }[];
  gapMark: string;
  clozeText: string;
}

export interface QuizGapwordFreeTextEntryData extends QuizFeedbackEntryBase {
  gaps: {
    referenceAnswer: string[];
  }[];
  gapMark: string;
  subType: "CLOZE_FREETEXT";
  clozeText: string;
  contentType: "QUIZ";
}

export interface QuizMultipleChoiceEntryData extends QuizFeedbackEntryBase {
  answers: string[];
  question: string;
  subType: "MULTIPLE_CHOICE" | "CODE_MULTIPLE_CHOICE";
  contentType: "QUIZ";
  // in the future this shouldnt be provided in the frontend
  correctAnswers?: boolean[];
  /** How many answers to pick when the answer key is not on the client. */
  selectCount?: number;
}

export interface QuizFreeTextEntryData {
  instruction: string;
  contentType: "QUIZ";
}

export interface ImageHotspotData {
  text: string;
  title: string;
  /** Fractional (0-1) position on the attached image. Only set once an image is attached. */
  x?: number;
  /** Fractional (0-1) position on the attached image. Only set once an image is attached. */
  y?: number;
  /** Free-text position, used only when no image is attached. */
  position?: string;
}

/** Older storyboards sent the description as a bare markdown string. */
export interface ImageDemonstrationData {
  title: string;
  description: string;
}

export interface ImageLabeledGraphicData {
  title: string;
  introduction?: string;
  text?: string;
  hotspots: ImageHotspotData[];
}

export interface StoryboardFile {
  id: string;
  url: string;
  filename: string;
  contentType: string;
}

export interface QuizMatchingEntryData extends QuizFeedbackEntryBase {
  pairs: {
    firstPart: string;
    secondPart: string;
  }[];
  subType: "MATCHING_DEFINITION" | "MATCHING_SENTENCE";
  contentType: "QUIZ";
}

// CODE types
export interface CodeExampleItem {
  text: string | null;
  sourceCode: string | null;
}

export interface CodeExampleData {
  /** Absent on storyboards generated before the titles rollout. */
  title?: string;
  sections: CodeExampleItem[];
}

/**
 * The same sections as a code example, but read as a walkthrough: the prose
 * carries the steps and the snippets are evidence inside them, so the block
 * has a title of its own instead of borrowing the first section's heading.
 */
export interface CodeExampleProblemSolvingData {
  title: string;
  sections: CodeExampleItem[];
}

export interface QuizCodeTrueFalseEntryData extends QuizFeedbackEntryBase {
  statement: string;
  // Older storyboards packed the code into `statement` as a markdown fence.
  codeSnippet?: string | null;
  correctAnswer: boolean;
}

export interface QuizCodeFreetextEntryData {
  challenge?: string;
  subtasks: string[];
  explanations: string[];
  solutions?: string[];
  /** Older storyboards use these names for `challenge` / `solutions`. */
  codeChallenge?: string;
  referenceSolutions?: string[];
}

export interface QuizCodeDebugEntryData {
  challenge?: string;
  codeWithBugs: string;
  errorMessage: string;
  explanation: string;
  solution?: string;
  /** Older storyboards use these names for `challenge` / `solution`. */
  debugChallenge?: string;
  codeSolution?: string;
}

export interface QuizCodeAssertionEntryData {
  challenge: string;
  codeStub: string;
  testCode: string;
  solution: string;
  implementationGoal?: string;
}

export interface QuizCodeExplanationEntryData {
  challenge: string;
  codeSnippet: string;
  explanation: string;
}

export interface CodeTheoryData {
  title: string;
  theory: string;
}

/**
 * A thinking prompt: a scenario the learner reads and a question to weigh up.
 * There is nothing to submit — no answers, no feedback, no validation.
 */
export interface CodeQuestionData {
  example: string;
  question: string;
}

// WORKSHEET types
export interface WorksheetTaskData {
  id: string;
  title: string;
  description: string;
  referenceAnswer: string;
}

export interface WorksheetData {
  title: string;
  introduction: string;
  scenario: string | null;
  tasks: WorksheetTaskData[];
  additionalTasks: WorksheetTaskData[];
}
